/**
 * LOGIN MODAL v2.0 - ULTRA OPTIMIZED
 * ✅ useCallback para handlers
 * ✅ React.memo en componentes
 * ✅ useMemo para beneficios
 * ✅ Mobile optimizations
 */
import { useState, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';

const LoginModal = memo(function LoginModal({ isOpen, onClose }) {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  // ✅ OPTIMIZADO: useMemo para beneficios (estáticos)
  const benefits = useMemo(() => [
    {
      icon: '🔄',
      color: 'blue',
      text: 'Sincronización automática entre dispositivos'
    },
    {
      icon: '👥',
      color: 'purple',
      text: 'Comparte con tu pareja o familia'
    },
    {
      icon: '☁️',
      color: 'green',
      text: 'Respaldo automático en la nube'
    }
  ], []);

  // ✅ OPTIMIZADO: useCallback para login
  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);
    const result = await login();
    setLoading(false);
    
    if (result.success) {
      onClose();
    } else {
      alert(`Error: ${result.error}`);
    }
  }, [login, onClose]);

  // ✅ OPTIMIZADO: useCallback para backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      
      <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-md w-full shadow-2xl border-2 border-gray-700/50">
        
        <CloseButton onClick={onClose} />
        
        <LoginHeader />
        
        <BenefitsList benefits={benefits} />
        
        <GoogleLoginButton
          onClick={handleGoogleLogin}
          loading={loading}
        />
        
        <SkipButton onClick={onClose} />
      </div>
    </div>
  );
});

// ✅ COMPONENTES MEMOIZADOS

const CloseButton = memo(function CloseButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-4 right-4 text-gray-400 hover:text-white transition active:scale-95"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
});

const LoginHeader = memo(function LoginHeader() {
  return (
    <div className="text-center mb-6">
      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
        <span className="text-3xl">🍽️</span>
      </div>
      <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
        Sincroniza con tu pareja
      </h2>
      <p className="text-gray-400 text-sm">
        Comparte tu planificación y lista de compras en tiempo real
      </p>
    </div>
  );
});

const BenefitsList = memo(function BenefitsList({ benefits }) {
  return (
    <div className="space-y-3 mb-6">
      {benefits.map((benefit, index) => (
        <BenefitItem key={index} {...benefit} />
      ))}
    </div>
  );
});

const BenefitItem = memo(function BenefitItem({ icon, color, text }) {
  const colorClasses = useMemo(() => ({
    blue: 'bg-blue-500/20',
    purple: 'bg-purple-500/20',
    green: 'bg-green-500/20'
  }), []);

  return (
    <div className="flex items-center gap-3 text-sm">
      <div className={`w-8 h-8 rounded-lg ${colorClasses[color]} flex items-center justify-center flex-shrink-0`}>
        <span className="text-lg">{icon}</span>
      </div>
      <p className="text-gray-300">{text}</p>
    </div>
  );
});

const GoogleLoginButton = memo(function GoogleLoginButton({ onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 active:scale-95"
    >
      {loading ? (
        <span>Cargando...</span>
      ) : (
        <>
          <GoogleIcon />
          <span>Continuar con Google</span>
        </>
      )}
    </button>
  );
});

const GoogleIcon = memo(function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
});

const SkipButton = memo(function SkipButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full mt-3 text-sm text-gray-400 hover:text-white transition active:scale-95"
    >
      Continuar sin sincronizar
    </button>
  );
});

export default LoginModal;